import type { LessonStandard } from "@shared/models/card-stack"
import type { CardAttrsBase } from "@shared/unified-tiptap/util/card"
import { findNodeAndPos } from "@shared/unified-tiptap/util/descendant-helpers"
import type { ExtensionCardGoogleClassroomAttrs } from "@tiptap-content/extensions/card-google-classroom/card-google-classroom-content-extension"
import type { ChainedCommands, Editor } from "@tiptap/core"
import { Fragment, type Node as PMNode } from "@tiptap/pm/model"
import { Converter } from "showdown"

const mdConverter = new Converter({
  strikethrough: true,
  tasklists: true,
  smoothLivePreview: true,
  disableForced4SpacesIndentedSublists: true,
  simpleLineBreaks: true,
  backslashEscapesHTMLTags: true,
})

/** Markdown as the assistant writes it (chat prose and card values alike), as HTML. */
export function markdownToHtml(markdown: string): string {
  return mdConverter.makeHtml(markdown)
}

/**
 * A standard the assistant wrote: it names one by id and our app fills in the rest
 * (see the backend's `@response_schema_unified`). Standards already in the card come back
 * carrying their whole `data-standard`, and are left alone.
 */
const AI_STANDARD_DIV = /<div\b[^>]*\bdata-content-type=["']standard["'][^>]*>(?:\s*<\/div>)?/gi

/**
 * The assistant's edits to one unified lesson, applied to the lesson's editor and shown as a
 * diff until the user accepts or rejects them.
 *
 * The legacy panel drives one AiValueEditor (a ScribeEditor) per card; a unified lesson is a
 * single editor holding every card, so a card edit is a content replacement addressed by card
 * id, and the AiChanges diff is toggled for the editor as a whole.
 */
export class AiLessonEdits {
  constructor(
    private readonly editor: Editor,
    /** Resolves a standard id from the course's standard sets. Null when it isn't one of them. */
    private readonly findStandard: (standardId: string) => LessonStandard | null,
  ) {}

  /**
   * Begin an editing session, diffing against the lesson as it is now. The diff stays hidden
   * until {@link showChanges}: a half-streamed card would otherwise read as a huge deletion.
   */
  start(): void {
    if (this.editor.isDestroyed) return

    this.editor.chain().setShowAiChanges(false).startTrackingAiChanges().run()
  }

  /** Show or hide the diff. Hide it again when the assistant starts revising. */
  showChanges(show: boolean): void {
    if (this.editor.isDestroyed) return

    this.editor.commands.setShowAiChanges(show)
  }

  setTitle(title: string): void {
    if (this.editor.isDestroyed) return

    const titleNode = this.editor.state.doc.firstChild
    if (titleNode?.type.name !== "lessonTitle") {
      console.error("AI changed the lesson title, but the lesson has none")
      return
    }

    this.aiEdit(
      this.editor.chain().command(({ tr, state }) => {
        tr.replaceWith(
          1,
          1 + titleNode.content.size,
          title ? state.schema.text(title) : Fragment.empty,
        )
        return true
      }),
    ).run()
  }

  /**
   * Replace the card's value — everything after its title — with `markdown`, the whole new
   * value as of this delta. `isDone` says whether the assistant has finished writing it.
   */
  setCardValue(cardId: string, markdown: string, isDone: boolean): void {
    if (this.editor.isDestroyed) return

    const found = findNodeAndPos(
      this.editor.state.doc,
      (node) => node.type.isInGroup("card") && (node.attrs as CardAttrsBase).id === cardId,
    )
    if (!found) {
      console.error(`AI returned card with non-existent ID ${cardId}, skipping`)
      return
    }

    const { node, pos } = found
    if (node.type.name === "cardGoogleClassroom") {
      this.setGoogleClassroomValue(pos, node, markdown, isDone)
      return
    }

    // titledCard holds its cardTitle first; untitledCard is all value.
    const from = node.type.name === "titledCard" ? pos + 1 + node.child(0).nodeSize : pos + 1
    const to = pos + node.nodeSize - 1
    const html = this.fillInStandards(markdownToHtml(markdown))

    this.aiEdit(
      this.editor.chain().insertContentAt(
        { from, to },
        // A card's content is `block+`, so an as-yet-empty value still needs a block.
        html.trim() === "" ? "<p></p>" : html,
        { updateSelection: false, applyInputRules: false, applyPasteRules: false },
      ),
    ).run()
  }

  /**
   * A Google Classroom card is an atom whose value is an attr, so there is nothing to stream
   * into: it takes the value whole, once the assistant has finished writing it. The value is
   * plain text here, not Markdown — that is what the Google Classroom sidebar edits and what
   * the post carries.
   *
   * Bumping `revision` is how an edit marks the post as needing an update, the same thing the
   * sidebar's own commits do.
   */
  private setGoogleClassroomValue(pos: number, node: PMNode, value: string, isDone: boolean): void {
    if (!isDone) return
    if (this.editor.isDestroyed) return

    const attrs = node.attrs as ExtensionCardGoogleClassroomAttrs
    this.aiEdit(
      this.editor.chain().command(({ tr }) => {
        tr.setNodeAttribute(pos, "value", value)
        tr.setNodeAttribute(pos, "revision", attrs.revision + 1)
        return true
      }),
    ).run()
  }

  /** Keep the edits. The caller persists the editor's new content. */
  accept(): void {
    if (this.editor.isDestroyed) return

    this.editor.chain().setShowAiChanges(false).stopTrackingAiChanges().run()
  }

  /** Put the lesson back the way it was when {@link start} ran. */
  reject(): void {
    if (this.editor.isDestroyed) return

    this.aiEdit(
      this.editor.chain().rejectAllAiChanges().setShowAiChanges(false).stopTrackingAiChanges(),
    ).run()
  }

  /**
   * Marks a chain as the assistant's own edit: it is not the user's typing (so it stays out of
   * the undo stack) and it must not be saved, since the user may still reject it —
   * UnifiedCardStackEditor skips its persistence for transactions carrying this meta.
   */
  private aiEdit(chain: ChainedCommands): ChainedCommands {
    return chain.setMeta("addToHistory", false).setMeta("noUnifiedCardStackAction", true)
  }

  private fillInStandards(html: string): string {
    const filled = html.replace(AI_STANDARD_DIV, (openTag) => {
      if (/\bdata-standard=/.test(openTag)) return openTag

      const standardId = /\bdata-id=["']([^"']*)["']/.exec(openTag)?.[1]
      const standard = standardId ? this.findStandard(standardId) : null
      if (!standard) {
        console.error(`AI added standardId ${standardId} but it was not found.`)
        return ""
      }
      return `<div data-content-type="standard" data-standard="${escapeAttribute(
        JSON.stringify(standard),
      )}"></div>`
    })

    return unwrapStandards(filled)
  }
}

const STANDARD_DIV_SOURCE = '<div data-content-type="standard"[^>]*></div>'
const PARAGRAPH = /<p>([^]*?)<\/p>/g
/** Splits on standards, keeping them: the pieces alternate prose, standard, prose, … */
const AROUND_STANDARD_DIV = new RegExp(`(${STANDARD_DIV_SOURCE})`)
const STANDARD_DIV = new RegExp(`^${STANDARD_DIV_SOURCE}$`)
/** What `simpleLineBreaks` leaves between standards Showdown put in one paragraph. */
const BLANK_OR_BREAK = /^(?:\s|<br\s*\/?>)*$/
/** The break that used to join prose to a standard, now that the standard has moved out. */
const EDGE_BREAK = /^\s*<br\s*\/?>\s*|\s*<br\s*\/?>\s*$/g

/**
 * Showdown wraps standards in a paragraph: on their own line each gets its own, and consecutive
 * lines share one, separated by the `<br />`s `simpleLineBreaks` writes. Left there, the HTML
 * parser hoists the block-level standards back out and the emptied paragraphs stay behind, so
 * lift them out here and keep only the paragraphs that still hold prose.
 */
function unwrapStandards(html: string): string {
  return html.replace(PARAGRAPH, (paragraph, inner: string) => {
    if (!AROUND_STANDARD_DIV.test(inner)) return paragraph

    return inner
      .split(AROUND_STANDARD_DIV)
      .map((piece) => {
        if (STANDARD_DIV.test(piece)) return piece
        return BLANK_OR_BREAK.test(piece) ? "" : `<p>${piece.replace(EDGE_BREAK, "")}</p>`
      })
      .join("")
  })
}

function escapeAttribute(value: string): string {
  // `>` too: the standard's own JSON must not end the tag the standard-div scans match.
  return value
    .replaceAll("&", "&amp;")
    .replaceAll('"', "&quot;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
}
